import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Link,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';

// Transcripts of a typical 30-second memo run 300-400 characters; the longest
// in production is about 1,500. Collapsing past this keeps a long memo from
// pushing the rest of the conversation off screen, without hiding the short
// ones that are the common case.
const COLLAPSE_THRESHOLD_CHARS = 280;

const COLOR_GREY_700 = '#8D8D8D';

/**
 * CON-1674 — renders a message's voice-memo transcript as its own labeled
 * section.
 *
 * Deliberately NOT merged into the message body. 12.3% of voice-memo messages
 * carry sender-typed text as well, and at least one of those corrects the memo
 * it is attached to — splicing machine output into a customer's own words
 * leaves the reader no way to tell which is which. The label is the whole
 * point: it marks where the human stops and the transcription starts.
 *
 * Rendered after the attachment and after any sender text, because the text is
 * the sender's framing of the memo.
 */
function TranscriptSection({
  transcription = '',
}) {
  const [expanded, setExpanded] = useState(false);

  const text = (transcription || '').trim();

  if (!text) {
    return null;
  }

  const isLong = text.length > COLLAPSE_THRESHOLD_CHARS;
  const visibleText = (isLong && !expanded)
    ? `${text.slice(0, COLLAPSE_THRESHOLD_CHARS).trimEnd()}…`
    : text;

  const handleToggle = () => {
    setExpanded((previous) => !previous);
  };

  return (
    <Box
      sx={{
        marginTop: '8px',
        paddingTop: '8px',
        borderTop: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.12)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginBottom: '2px',
        }}
      >
        <GraphicEqIcon
          sx={{ fontSize: '14px', color: COLOR_GREY_700 }}
        />
        <Typography
          component="span"
          variant="caption"
          sx={{
            color: COLOR_GREY_700,
            fontWeight: 600,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          }}
        >
          Transcription
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-line',
          wordBreak: 'break-word',
          fontStyle: 'italic',
          userSelect: 'text',
          WebkitUserSelect: 'text',
        }}
      >
        {visibleText}
      </Typography>

      {isLong && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            marginTop: '2px',
          }}
        >
          {expanded
            ? <ExpandLessIcon fontSize="small" color="primary" />
            : <ExpandMoreIcon fontSize="small" color="primary" />}
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="always"
            onClick={handleToggle}
            sx={{ fontWeight: 600 }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Link>
        </Box>
      )}
    </Box>
  );
}

TranscriptSection.propTypes = {
  transcription: PropTypes.string,
};

export default TranscriptSection;
